import type { WfhReportFilters, WfhReportRow } from '../types/wfhReport';
import type { ReportPreviewRow } from '../types/report';
import { calendarDateFromApi, formatTime } from './dateTime';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const EMPTY = '—';

const EMPLOYEE_KEYS = [
  'employeeName',
  'employee_name',
  'employeeFullName',
  'displayName',
  'display_name',
  'fullName',
  'name',
  'employee',
];
const EMPLOYEE_NUMBER_KEYS = ['employeeNumber', 'employeeCode'];
const DEPARTMENT_KEYS = ['departmentName', 'department', 'departmentId'];
const DATE_KEYS = [
  'wfhDate',
  'attendanceDate',
  'workDate',
  'date',
  'fromDate',
  'startDate',
];
const CHECK_IN_KEYS = [
  'firstCheckInAt',
  'firstCheckInLocal',
  'checkInTime',
  'checkIn',
  'checkInLocalTime',
  'inTime',
  'punchInTime',
];
const CHECK_OUT_KEYS = [
  'lastCheckOutAt',
  'lastCheckOutLocal',
  'checkOutTime',
  'checkOut',
  'checkOutLocalTime',
  'outTime',
  'punchOutTime',
];

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function firstValue(
  values: Record<string, unknown>,
  keys: string[],
): unknown {
  for (const key of keys) {
    if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
      return values[key];
    }
  }
  return null;
}

function asDisplayText(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || EMPTY;
  }
  return EMPTY;
}

function asTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return EMPTY;
    const formatted = formatTime(trimmed);
    return formatted === 'Not available' ? trimmed : formatted;
  }
  return asDisplayText(value);
}

function parseDateParts(
  value: string,
): { year: number; month: number; day: number } | null {
  const match = value.trim().match(DATE_ONLY);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  return { year, month, day };
}

/** Design label e.g. "01 May 2024". */
export function formatWfhReportDateLabel(value: string): string {
  const iso = calendarDateFromApi(value) ?? value.trim();
  const parts = parseDateParts(iso);
  if (!parts) return value;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${pad2(parts.day)} ${months[parts.month - 1]} ${parts.year}`;
}

export function formatWfhReportDateRangeLabel(
  fromDate: string,
  toDate: string,
): string {
  return `${formatWfhReportDateLabel(fromDate)} - ${formatWfhReportDateLabel(toDate)}`;
}

export function createDefaultWfhReportFilters(
  now = new Date(),
): WfhReportFilters {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  return {
    fromDate: `${year}-${pad2(month)}-01`,
    toDate: `${year}-${pad2(month)}-${pad2(day)}`,
    department: '',
  };
}

export function validateWfhReportDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!parseDateParts(fromDate.trim())) {
    return 'Use a YYYY-MM-DD from date.';
  }
  if (!parseDateParts(toDate.trim())) {
    return 'Use a YYYY-MM-DD to date.';
  }
  if (toDate.trim() < fromDate.trim()) {
    return 'To date cannot be earlier than from date.';
  }
  return null;
}

export function wfhSummaryApiFilters(
  fromDate: string,
  toDate: string,
): { fromDate: string; toDate: string } | null {
  if (validateWfhReportDateRange(fromDate, toDate)) return null;
  return { fromDate: fromDate.trim(), toDate: toDate.trim() };
}

export function mapWfhReportRows(rows: ReportPreviewRow[]): WfhReportRow[] {
  return rows.map((row, index) => {
    const values = row.values;
    const employeeName = asDisplayText(firstValue(values, EMPLOYEE_KEYS));
    const employeeNumber = asDisplayText(
      firstValue(values, EMPLOYEE_NUMBER_KEYS),
    );
    const employee = employeeName !== EMPTY ? employeeName : employeeNumber;

    const rawDate = firstValue(values, DATE_KEYS);
    const date = calendarDateFromApi(rawDate) ?? asDisplayText(rawDate);
    const departmentValue = firstValue(values, DEPARTMENT_KEYS);

    return {
      rowKey: row.rowKey || `wfh-report-${index}`,
      employee,
      date: date === EMPTY ? '' : date,
      dateLabel: date === EMPTY ? EMPTY : formatWfhReportDateLabel(date),
      checkIn: asTime(firstValue(values, CHECK_IN_KEYS)),
      checkOut: asTime(firstValue(values, CHECK_OUT_KEYS)),
      department:
        departmentValue === null ? null : asDisplayText(departmentValue),
    };
  });
}

export function filterWfhReportRows(
  rows: WfhReportRow[],
  filters: Pick<WfhReportFilters, 'department'>,
): WfhReportRow[] {
  const department = filters.department.trim().toLowerCase();
  if (!department) return rows;
  return rows.filter(
    row =>
      row.department != null &&
      row.department.trim().toLowerCase() === department,
  );
}

export function summarizeWfhReportRows(rows: WfhReportRow[]): {
  totalWfhDays: number;
  employees: number;
  departments: number;
} {
  const employeeNames = new Set<string>();
  const departmentNames = new Set<string>();
  for (const row of rows) {
    if (row.employee && row.employee !== EMPTY) {
      employeeNames.add(row.employee.trim().toLowerCase());
    }
    if (row.department && row.department !== EMPTY) {
      departmentNames.add(row.department.trim().toLowerCase());
    }
  }
  return {
    totalWfhDays: rows.length,
    employees: employeeNames.size,
    departments: departmentNames.size,
  };
}
